import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createSelector } from 'reselect';
import { Map, List } from 'immutable';
import Helmet from 'react-helmet';
import classNames from 'classnames';
import { replace } from 'connected-react18-router';

import { Contact } from '../components/Contact';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import { unwrap, isDeepChanged, getChanged, getIn, getOriginalIn } from '../utils/ChangeSpy';
import config from '../config';
import slugify from '../utils/slugify';
import deletionCheck from '../utils/deletingPermission';
import { isResearcher } from '../utils/checkPermissions';
import stopProp from '../decorators/stopPropagation';
import { publish } from '../decorators/tabChange';
import {
  loadContactDetail,
  saveContactDetail,
  saveContactPhone,
  changeContactInfoField,
  addContactField,
  deleteContactField,
  editContactField,
  changeContactField,
  saveContactField,
  revertContactField,
  stopEditContactField,
  saveContactEmail,
  saveContactAddress,
  deleteContact,
  closeValidationError,
  reorderContactField,
} from '../actions/contactDetail';
import { fetchEventActivities } from '../actions/eventActivities';
import { enterContactEvent, deleteContactEvent, leaveContactEvent } from '../actions/contact/contactEvents';
import { saveContactCompany } from '../actions/contact/contactExecutive';
import { showError } from '../utils/MessagePopup';
import naDeletionCheck from './services/deletionCheck';
import AutoComplete from './AutoComplete';
import confirm from './decorators/confirm';
import ContactExecutiveCreate from './Contact/ContactExecutiveCreate';
import ContactLpCreate from './Contact/ContactLpCreate';
import { PrettyErrorProvider } from '../contexts/PrettyErrorContext';
import { usePopupContext } from '../contexts/PopupContext';
import { EntityEventContext } from '../contexts/EntityEventContext';

const tabs = config.tabs.get('contact');
const values = config.values.get('contact');

// First path segment after `/contact/:contactId/` for each real tab route (see ContactRouting.js).
// Used to tell an optional `contact_slug` segment apart from a real tab segment when the URL only
// has one extra segment (e.g. `/contact/123/target` vs `/contact/123/john-doe`).
const CONTACT_TABS = ['target', 'buyer', 'executive', 'lp'];

/**
 * Split the part of `pathname` after `/contact/:contactId` into its pieces, and work out
 * whether the first piece is a `contact_slug` (readability only, not a real route segment)
 * or the start of a real tab route (`target`/`buyer`/`executive`/`lp`).
 *
 * Returns `{ basePrefix, currentSlug, tail, isBare }`, mirroring `parseCompanyPath` in
 * `containers/Company.js`.
 */
const parseContactPath = (contactId, pathname) => {
  const basePrefix = `/contact/${contactId}`;
  const segments = pathname
    .slice(basePrefix.length)
    .split('/')
    .filter(Boolean);
  const hasSlug = segments.length > 0 && !CONTACT_TABS.includes(segments[0]);
  const tail = hasSlug ? segments.slice(1) : segments;

  return {
    basePrefix,
    currentSlug: hasSlug ? segments[0] : null,
    tail,
    isBare: tail.length === 0,
  };
};

const handleAddTarget = () => {
  window.open('/company/new/target');
};

const handleAddBuyer = () => {
  window.open('/company/new/buyer');
};

const handleAddExecutive = () => {
  window.open('/contact/new/executive');
};

const handleAddLp = () => {
  window.open('/contact/new/lp');
};

/**
 *  Contact page container.
 *  Change title to `"Contact Page"`.
 *  Note: Tabs are subroutes.
 */
export const ContactContainer = props => {
  const {
    children,
    match,
    location,
    history,
    user,
    contactInfo,
    contactEvents,
    showSave,
    inputErrors,
    formError,
    backloggedActivities,
    notifyTab,
    loadContactDetail,
    saveContactDetail,
    saveContactPhone,
    changeContactInfoField,
    addContactField,
    deleteContactField,
    editContactField,
    changeContactField,
    revertContactField,
    stopEditContactField,
    saveContactEmail,
    saveContactAddress,
    deleteContact,
    closeValidationError,
    reorderContactField,
    fetchEventActivities,
    enterContactEvent,
    leaveContactEvent,
    deleteContactEvent,
    replace,
  } = props;
  const { contactId } = match.params;
  const { openPopup, closePopup, onClosePopup } = usePopupContext();
  const hasAccess = user ? !isResearcher(user.getIn(['roles', 0, 'slug'], null)) : false;

  const onSaveCallbacksRef = useRef(new Set());

  const addOnSaveCallback = useCallback(callback => {
    onSaveCallbacksRef.current.add(callback);
  }, []);

  const removeOnSaveCallback = useCallback(callback => {
    onSaveCallbacksRef.current.delete(callback);
  }, []);

  const handleChangeField = useCallback(
    ({ field, name, value, index }) => {
      changeContactField({ field, name, value, index });
    },
    [changeContactField],
  );

  const handleChangeEmail = useCallback(
    (event, index) => {
      const {
        target: { name: fieldName, value },
      } = event;
      const name = fieldName.split('.').pop();

      handleChangeField({ field: 'emails', name, value, index });
    },
    [handleChangeField],
  );

  const handleChangePhone = useCallback(
    (event, index) => {
      const {
        target: { name: fieldName, value },
      } = event;
      const name = fieldName.split('.').pop();

      handleChangeField({ field: 'phones', name, value, index });
    },
    [handleChangeField],
  );

  const handleChangeAddress = useCallback(
    (event, index) => {
      const {
        target: { name: fieldName, value },
      } = event;
      const name = fieldName.split('.').pop();

      handleChangeField({ field: 'addresses', name, value, index });
    },
    [handleChangeField],
  );

  const editField = useCallback(
    (field, index) => {
      if (contactInfo.getIn([field, index, 'isInherited'])) return;

      editContactField({ field, index });
    },
    [contactInfo, editContactField],
  );

  const handleEditEmail = useCallback(
    (mouseEvent, index) => {
      editField('emails', index);
    },
    [editField],
  );

  const handleEditPhone = useCallback(
    (mouseEvent, index) => {
      editField('phones', index);
    },
    [editField],
  );

  const handleEditAddress = useCallback(
    (mouseEvent, index) => {
      editField('addresses', index);

      const name = 'EditAddressPopup';
      const popupProps = { contactId, onChange: handleChangeAddress, index };

      openPopup(name, popupProps);
      onClosePopup(revertContactField);
      onClosePopup(stopEditContactField);
    },
    [
      editField,
      contactId,
      handleChangeAddress,
      openPopup,
      onClosePopup,
      revertContactField,
      stopEditContactField,
    ],
  );

  const addField = useCallback(
    field => {
      const list = contactInfo.get(field);
      const inheritedIndex = list.findIndex(item => item.get('isInherited'));
      const newItemIndex = inheritedIndex !== -1 ? inheritedIndex : list.size;

      addContactField({ field });
      editContactField({ field, index: newItemIndex });
    },
    [contactInfo, addContactField, editContactField],
  );

  const handleAddAddress = useCallback(() => {
    addField('addresses');
    handleEditAddress(null, contactInfo.get('addresses').size);
  }, [addField, contactInfo, handleEditAddress]);

  const handleAddEmail = useCallback(() => {
    addField('emails');
  }, [addField]);

  const handleAddPhone = useCallback(() => {
    addField('phones');
  }, [addField]);

  const deleteField = useCallback(
    ({ field, index }) => {
      const fieldId = contactInfo.getIn([field, index, 'id'], 'new');

      deleteContactField({ id: contactId, field, fieldId, index, afterSuccess: closePopup });
    },
    [contactInfo, contactId, deleteContactField, closePopup],
  );

  const deleteAddressAfterConfirm = useCallback(
    (mouseEvent, index) => {
      deleteField({ field: 'addresses', index });
    },
    [deleteField],
  );

  const handleAddressDelete = useMemo(
    () =>
      stopProp(
        deletionCheck(user, confirm('Delete this address?', deleteAddressAfterConfirm, { openPopup, closePopup }), {
          openPopup,
          closePopup,
        }),
      ),
    [user, deleteAddressAfterConfirm, openPopup, closePopup],
  );

  const deleteEmailAfterConfirm = useCallback(
    (mouseEvent, index) => {
      deleteField({ field: 'emails', index });
    },
    [deleteField],
  );

  const handleEmailDelete = useMemo(
    () =>
      stopProp(
        deletionCheck(user, confirm('Delete this email?', deleteEmailAfterConfirm, { openPopup, closePopup }), {
          openPopup,
          closePopup,
        }),
      ),
    [user, deleteEmailAfterConfirm, openPopup, closePopup],
  );

  const deletePhoneAfterConfirm = useCallback(
    (mouseEvent, index) => {
      deleteField({ field: 'phones', index });
    },
    [deleteField],
  );

  const handlePhoneDelete = useMemo(
    () =>
      stopProp(
        deletionCheck(user, confirm('Delete this phone?', deletePhoneAfterConfirm, { openPopup, closePopup }), {
          openPopup,
          closePopup,
        }),
      ),
    [user, deletePhoneAfterConfirm, openPopup, closePopup],
  );

  const deleteContactAfterConfirm = useCallback(() => {
    deleteContact({ contactId, afterSuccess: closePopup });
  }, [contactId, deleteContact, closePopup]);

  const handleContactDelete = useMemo(
    () =>
      stopProp(
        deletionCheck(user, confirm('Delete this contact?', deleteContactAfterConfirm, { openPopup, closePopup }), {
          openPopup,
          closePopup,
        }),
      ),
    [user, deleteContactAfterConfirm, openPopup, closePopup],
  );

  const handleSortPhone = useCallback(
    (data, oldIndex, newIndex) => {
      const phoneValues = data
        .map((value, index) => ({
          id: value.id,
          isInherited: value.isInherited,
          type: unwrap(value.type),
          priority: index,
          contactId: value.contactId,
          phone: unwrap(value.value),
        }))
        .filter(value => value.isInherited === false);

      reorderContactField({ field: 'phones', oldIndex, newIndex, contactId, data: phoneValues });
    },
    [contactId, reorderContactField],
  );

  const handleSortEmail = useCallback(
    (data, oldIndex, newIndex) => {
      const emailValues = data.map((value, index) => ({
        id: value.id,
        type: unwrap(value.type),
        priority: index,
        contactId: value.contactId,
        deletedAt: value.deletedAt,
        email: unwrap(value.value),
      }));

      reorderContactField({ field: 'emails', oldIndex, newIndex, contactId, data: emailValues });
    },
    [contactId, reorderContactField],
  );

  const handleEventMouseEnter = useCallback(
    (event, eventId) => {
      const { target, clientX: left } = event;
      const rect = target.getBoundingClientRect();
      const { bottom: top } = rect;

      enterContactEvent({ eventId, left, top });
    },
    [enterContactEvent],
  );

  const handleEventMouseLeave = useCallback(
    (event, eventId) => {
      leaveContactEvent({ eventId });
    },
    [leaveContactEvent],
  );

  const handleChange = useCallback(
    event => {
      const { name } = event.target;

      switch (event.target.type) {
        case 'checkbox':
          return changeContactInfoField({
            name,
            value: event.target.checked,
          });

        default:
          return changeContactInfoField({
            name,
            value: event.target.value,
          });
      }
    },
    [changeContactInfoField],
  );

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleAddEvent = useCallback(() => {
    const name = 'EventContactPopup';
    const isExecutiveRoute = window.location.pathname.includes('/executive');

    if (!isExecutiveRoute) return;

    openPopup(name, {
      contactId,
      event: null,
      approach: 'executive',
      noteText: 'Notes',
      onReopen: handleAddEvent,
    });
  }, [contactId, openPopup]);

  const handleEditEvent = useCallback(
    (mouseEvent, event) => {
      const name = 'EventContactPopup';

      if (event.get('id') && !event.get('meta')) {
        const isExecutiveRoute = window.location.pathname.includes('/executive');

        openPopup(name, {
          contactId,
          event,
          noteText: isExecutiveRoute ? 'Notes' : undefined,
          onReopen: handleAddEvent,
        });
      }
    },
    [contactId, openPopup, handleAddEvent],
  );

  const deleteEventAfterConfirm = useCallback(
    (mouseEvent, event) => {
      const { id } = event;

      deleteContactEvent({
        id,
        contactId,
        afterSuccess: () => {
          notifyTab({ id: event.id }, 'onEventChange');
          closePopup();
        },
      });
    },
    [contactId, deleteContactEvent, notifyTab, closePopup],
  );

  const handleDeleteEvent = useMemo(
    () =>
      stopProp(
        naDeletionCheck(user, confirm('Delete this event?', deleteEventAfterConfirm, { openPopup, closePopup }), {
          openPopup,
          closePopup,
        }),
      ),
    [user, deleteEventAfterConfirm, openPopup, closePopup],
  );

  const submitPhones = useCallback(() => {
    const phonesToSend = contactInfo.get('phones').filter(phone => isDeepChanged(phone));

    phonesToSend.forEach(phone => {
      const body = getChanged(phone);
      const id = phone.get('id');

      if ('value' in body) body.phone = body.value;
      body.priority = phone.get('priority');
      saveContactPhone({ contactId, id, body });
    });
  }, [contactInfo, contactId, saveContactPhone]);

  const submitEmails = useCallback(() => {
    const emailsToSend = contactInfo.get('emails').filter(email => isDeepChanged(email));

    emailsToSend.forEach(email => {
      const body = getChanged(email);
      const id = email.get('id');

      if ('value' in body) body.email = body.value;
      body.priority = email.get('priority');
      saveContactEmail({ contactId, id, body });
    });
  }, [contactInfo, contactId, saveContactEmail]);

  const submitAddresses = useCallback(() => {
    if (contactInfo.getIn(['editing', 0]) === 'addresses') return;

    const addressesToSend = contactInfo.get('addresses').filter(address => isDeepChanged(address));

    addressesToSend.forEach(address => {
      const body = getChanged(address);
      const id = address.get('id');

      saveContactAddress({ contactId, id, body });
    });
  }, [contactInfo, contactId, saveContactAddress]);

  const handleBlurInfo = useCallback(() => {
    if (contactInfo.get('isValid')) {
      const contact = getChanged(contactInfo);

      if (Object.keys(contact).length) {
        if (contact.prefix) contact.prefix = contact.prefix.get('value');
        if (contact.suffix) contact.suffix = contact.suffix.get('value');
        saveContactDetail({ id: contactId, contact });
      }
    }
    submitPhones();
    submitEmails();
    submitAddresses();
  }, [contactInfo, contactId, saveContactDetail, submitPhones, submitEmails, submitAddresses]);

  const handleSave = useCallback(
    event => {
      handleBlurInfo(event);
      onSaveCallbacksRef.current.forEach(callback => callback(event));

      if (formError.size) {
        openPopup('ErrorPopup', { formError });
      }
    },
    [handleBlurInfo, formError, openPopup],
  );

  const handleSubmitInfo = useCallback(event => {
    event.preventDefault();
  }, []);

  const handleChangeSuggestion = useCallback(
    suggestion => {
      if (typeof suggestion.value === 'string') {
        const { value } = suggestion;
        const nextValue = contactInfo.get('suffixes').find(
          suffix => suffix.get('name') === value,
          null,
          Map({
            name: value || '',
            value: value || '',
          }),
        );

        changeContactInfoField({ name: 'suffix', value: nextValue });
      }
    },
    [contactInfo, changeContactInfoField],
  );

  const trySlugRedirect = () => {
    if (isNaN(Number(contactId))) return;
    if (contactInfo.get('loading')) return;

    const desiredSlug = slugify(getOriginalIn(contactInfo, 'fullName', ''));

    if (!desiredSlug) return;

    const { basePrefix, currentSlug, tail } = parseContactPath(contactId, location.pathname);

    if (currentSlug === desiredSlug) return;

    const newPathname = `${basePrefix}/${desiredSlug}${tail.length ? `/${tail.join('/')}` : ''}`;

    if (newPathname !== location.pathname) replace(`${newPathname}${location.search}`);
  };

  const tryRedirect = () => {
    if (contactInfo.get('isExecutive')) return setTimeout(() => replace(`/contact/${contactId}/executive`), 0);
    if (contactInfo.get('hasTargets')) return setTimeout(() => replace(`/contact/${contactId}/target`), 0);
    if (contactInfo.get('hasBuyers')) return setTimeout(() => replace(`/contact/${contactId}/buyer`), 0);
    if (contactInfo.get('isLP')) return setTimeout(() => replace(`/contact/${contactId}/lp`), 0);
  };

  useEffect(() => {
    loadContactDetail({ id: contactId });
    fetchEventActivities();

    if (!hasAccess) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });
    }
  }, []);

  const hasMountedRef = useRef(false);
  const previousContactInfoRef = useRef(contactInfo);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousContactInfoRef.current = contactInfo;

      return;
    }

    const previousContactInfo = previousContactInfoRef.current;

    previousContactInfoRef.current = contactInfo;

    trySlugRedirect();

    if (contactInfo.get('loading') === true) return;
    if (previousContactInfo.get('loading') === false) return;

    if (parseContactPath(contactId, location.pathname).isBare) {
      tryRedirect();
    }
  });

  // Mirrors the original `createSelector(props => props.contactEvents, ...)`: memoized on
  // `contactEvents` ONLY, matching reselect's own memoization key exactly - so, like the
  // original, `backloggedActivities` is deliberately allowed to go stale here until
  // `contactEvents` next changes (a pre-existing quirk, not something introduced by this
  // conversion - `onMouseEnter`/`onMouseLeave` never go stale either way since they're stable).
  const contactEventProps = useMemo(
    () => ({
      events: contactEvents,
      onMouseEnter: handleEventMouseEnter,
      onMouseLeave: handleEventMouseLeave,
      backloggedActivities,
    }),
    [contactEvents],
  );

  const entityEventContextValue = useMemo(
    () => ({
      onAddAddress: handleAddAddress,
      onAddEmail: handleAddEmail,
      onAddPhone: handleAddPhone,
      onEditEmail: handleEditEmail,
      onEditPhone: handleEditPhone,
      onEditAddress: handleEditAddress,
      onChangeEmail: handleChangeEmail,
      onChangePhone: handleChangePhone,
      onAddEvent: handleAddEvent,
      onChangeAddress: handleChangeAddress,
      onEditEvent: handleEditEvent,
      onDeleteEvent: handleDeleteEvent,
      onAddressDelete: handleAddressDelete,
      onEmailDelete: handleEmailDelete,
      onPhoneDelete: handlePhoneDelete,
      addOnSaveCallback,
      removeOnSaveCallback,
      onSortPhone: handleSortPhone,
      onSortEmail: handleSortEmail,
    }),
    [
      handleAddAddress,
      handleAddEmail,
      handleAddPhone,
      handleEditEmail,
      handleEditPhone,
      handleEditAddress,
      handleChangeEmail,
      handleChangePhone,
      handleAddEvent,
      handleChangeAddress,
      handleEditEvent,
      handleDeleteEvent,
      handleAddressDelete,
      handleEmailDelete,
      handlePhoneDelete,
      addOnSaveCallback,
      removeOnSaveCallback,
      handleSortPhone,
      handleSortEmail,
    ],
  );

  if (!hasAccess) return null;

  const loading = contactInfo.get('loading');
  const { currentSlug: contactSlug } = parseContactPath(contactId, location.pathname);
  const params = { ...match.params, contactSlug };

  const renderContactContent = () => {
    if (children) return children;
    if (location.pathname === '/contact/new/executive') return <ContactExecutiveCreate />;
    if (location.pathname === '/contact/new/lp') return <ContactLpCreate />;

    return null;
  };

  const getSuffixesSuggest = () => {
    const value = getIn(contactInfo, 'suffix').get('name');
    const idValue = value;
    const regExp = new RegExp(String(value).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1'), 'i');
    const className = classNames('form-control input-sm', {
      changed: isDeepChanged(contactInfo.get('suffix')),
    });
    const suggestions = getIn(contactInfo, 'suffixes')
      .filter(suffix => regExp.test(suffix.get('name')))
      .map(suffix => ({
        id: suffix.get('value'),
        text: suffix.get('name'),
      }));

    return (
      <AutoComplete
        idValue={idValue}
        inputProps={{ className }}
        loading={false}
        name="suffix"
        onChange={handleChangeSuggestion}
        onFind={args => {
          setTimeout(
            () =>
              args.afterSuccess({
                response: {
                  data: getIn(contactInfo, 'suffixes')
                    .filter(suffix => regExp.test(suffix.get('name')))
                    .map(suffix => ({
                      id: suffix.get('value'),
                      text: suffix.get('name'),
                    })),
                },
              }),
            0,
          );
        }}
        suggestions={suggestions}
        suggestionsName="suffixes"
        value={value}
        suggestIfEmpty
      />
    );
  };

  return (
    <div className="full-height">
      <Helmet title={`${getIn(contactInfo, 'fullName', '')} [Contact Page]`.trim()} />
      <EntityEventContext.Provider value={entityEventContextValue}>
        <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
          <Contact
            {...props}
            contactEventProps={contactEventProps}
            contactInfo={contactInfo}
            loading={loading}
            onBlurInfo={handleBlurInfo}
            onChange={handleChange}
            onDelete={handleContactDelete}
            onNewBuyer={handleAddBuyer}
            onNewExecutive={handleAddExecutive}
            onNewLp={handleAddLp}
            onNewTarget={handleAddTarget}
            onSave={handleSave}
            onSubmitInfo={handleSubmitInfo}
            params={params}
            showSave={showSave}
            suffixSuggest={getSuffixesSuggest()}
          >
            {!loading ? renderContactContent() : null}
          </Contact>
        </PrettyErrorProvider>
      </EntityEventContext.Provider>
    </div>
  );
};

const changedInfoSelector = createSelector(
  state => state.contact.info,
  info => isDeepChanged(info),
);

const changedLpSelector = createSelector(
  state => state.contact.lp,
  lp => isDeepChanged(lp),
);

const changedExecSelector = createSelector(
  state => state.contact.executive,
  executive => isDeepChanged(executive),
);

const mapStateToProps = state => {
  const formError = (state.contact.info.get('inputErrors') || List()).concat(
    state.contact.executive.get('inputErrors') || List(),
  );

  return {
    formError,
    inputErrors: state.contact.info.get('inputErrors'),
    contactInfo: state.contact.info,
    contactEvents: state.contact.events,
    showSave: changedInfoSelector(state) || changedLpSelector(state) || changedExecSelector(state),
    tabs,
    values,
    user: state.auth.get('user', null),
    backloggedActivities: state.eventActivities.get('backloggedActivityValues', List()),
  };
};

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      fetchEventActivities,
      loadContactDetail,
      changeContactInfoField,
      saveContactDetail,
      addContactField,
      editContactField,
      changeContactField,
      saveContactField,
      revertContactField,
      stopEditContactField,
      enterContactEvent,
      leaveContactEvent,
      deleteContactEvent,
      saveContactCompany,
      deleteContactField,
      saveContactPhone,
      saveContactEmail,
      saveContactAddress,
      deleteContact,
      closeValidationError,
      reorderContactField,
      replace,
    },
    dispatch,
  ),
});

ContactContainer.propTypes = {
  changeContactInfoField: PropTypes.func.isRequired,
  loadContactDetail: PropTypes.func.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(publish(memo(ContactContainer)));
